import axios from "axios";
import { Capacitor, CapacitorHttp } from "@capacitor/core";
import { DEFAULT_LANGUAGE, UI_LANGUAGE_STORAGE_KEY } from "../i18n";

const API_URL_STORAGE_KEY = "pennypantry_api_url";
const LEGACY_API_URL_STORAGE_KEY = "priceradar_api_url";
const PRODUCTION_API_BASE_URL = "https://api.pennypantryapp.com";
const PRIVATE_HOST_PATTERNS = [
  /^localhost$/i,
  /^127\./,
  /^10\./,
  /^192\.168\./,
  /^172\.(1[6-9]|2\d|3[0-1])\./,
];

function normalizeBaseUrl(url) {
  return url.replace(/\/+$/, "");
}

function getConfiguredBaseUrl(...values) {
  for (const value of values) {
    const normalized = value?.trim();
    if (normalized) {
      return normalizeBaseUrl(normalized);
    }
  }
  return "";
}

function parseUrl(url) {
  try {
    return new URL(url);
  } catch (error) {
    return null;
  }
}

function isPrivateOrInsecureUrl(url) {
  const parsedUrl = parseUrl(url);
  if (!parsedUrl) {
    return true;
  }

  return (
    parsedUrl.protocol !== "https:" ||
    PRIVATE_HOST_PATTERNS.some((pattern) => pattern.test(parsedUrl.hostname))
  );
}

function shouldUseStoredApiOverride(storedUrl, configuredUrl) {
  if (!storedUrl) {
    return false;
  }

  const configuredApiUrl = configuredUrl || PRODUCTION_API_BASE_URL;
  if (configuredApiUrl.startsWith("https://") && isPrivateOrInsecureUrl(storedUrl)) {
    try {
      window.localStorage?.removeItem(API_URL_STORAGE_KEY);
      window.localStorage?.removeItem(LEGACY_API_URL_STORAGE_KEY);
    } catch (error) {
      // Ignore storage cleanup errors; the important part is not using an unsafe URL.
    }
    return false;
  }

  return true;
}

function isNativeRuntime() {
  if (typeof window === "undefined") {
    return false;
  }

  const protocol = window.location?.protocol || "";
  if (protocol === "capacitor:" || protocol === "ionic:") {
    return true;
  }

  try {
    return Boolean(Capacitor?.isNativePlatform?.());
  } catch (error) {
    return false;
  }
}

function resolveApiBaseUrl() {
  const defaultApiPort = process.env.REACT_APP_API_PORT?.trim() || "8002";
  const nativeEnvUrl = getConfiguredBaseUrl(
    process.env.REACT_APP_NATIVE_API_URL,
    process.env.REACT_APP_API_URL
  );
  const browserEnvUrl = getConfiguredBaseUrl(process.env.REACT_APP_API_URL);
  const localStorageOverride =
    typeof window !== "undefined"
      ? getConfiguredBaseUrl(
          window.localStorage?.getItem(API_URL_STORAGE_KEY),
          window.localStorage?.getItem(LEGACY_API_URL_STORAGE_KEY)
        )
      : "";

  if (isNativeRuntime()) {
    if (shouldUseStoredApiOverride(localStorageOverride, nativeEnvUrl)) {
      return localStorageOverride;
    }
    if (nativeEnvUrl) {
      return nativeEnvUrl;
    }
  }

  if (typeof window !== "undefined") {
    const { hostname, origin, protocol } = window.location;
    if (browserEnvUrl) {
      return browserEnvUrl;
    }
    if (hostname && hostname !== "localhost" && hostname !== "127.0.0.1") {
      if (protocol === "https:") {
        return normalizeBaseUrl(origin);
      }
      return `http://${hostname}:${defaultApiPort}`;
    }
  }

  if (process.env.NODE_ENV === "production") {
    return PRODUCTION_API_BASE_URL;
  }

  return `http://127.0.0.1:${defaultApiPort}`;
}

function getUiLanguage() {
  if (typeof window === "undefined") {
    return DEFAULT_LANGUAGE;
  }

  return window.localStorage?.getItem(UI_LANGUAGE_STORAGE_KEY) || DEFAULT_LANGUAGE;
}

function buildAbsoluteUrl(url) {
  if (!url) {
    return API_BASE_URL;
  }
  if (/^https?:\/\//i.test(url)) {
    return url;
  }
  const normalizedPath = url.startsWith("/") ? url : `/${url}`;
  return `${API_BASE_URL}${normalizedPath}`;
}

function isFormDataPayload(data) {
  return typeof FormData !== "undefined" && data instanceof FormData;
}

function normalizeResponseData(response) {
  const contentType =
    response?.headers?.["content-type"] ||
    response?.headers?.["Content-Type"] ||
    "";
  const data = response?.data;

  if (typeof data === "string" && contentType.includes("application/json")) {
    try {
      return JSON.parse(data);
    } catch (error) {
      return data;
    }
  }

  return data;
}

function createNativeError(message, response, code = "ERR_NETWORK") {
  const error = new Error(message);
  error.code = code;
  if (response) {
    error.response = response;
  }
  return error;
}

const browserApi = axios.create({
  baseURL: resolveApiBaseUrl(),
  timeout: 30000,
});

export const API_BASE_URL = resolveApiBaseUrl();

async function nativeRequest(method, url, data, config = {}) {
  const absoluteUrl = buildAbsoluteUrl(url);
  const headers = { ...(config.headers || {}) };
  const requestConfig = {
    url: absoluteUrl,
    method: method.toUpperCase(),
    headers,
    params: config.params,
    connectTimeout: config.timeout || 30000,
    readTimeout: config.timeout || 30000,
  };

  if (data !== undefined) {
    requestConfig.data = data;
    if (isFormDataPayload(data)) {
      requestConfig.dataType = "formData";
    } else if (!headers["Content-Type"] && !headers["content-type"]) {
      requestConfig.headers = {
        ...headers,
        "Content-Type": "application/json",
      };
    }
  }

  try {
    const rawResponse = await CapacitorHttp.request(requestConfig);
    const response = {
      data: normalizeResponseData(rawResponse),
      status: rawResponse.status,
      headers: rawResponse.headers || {},
      config: {
        ...config,
        baseURL: API_BASE_URL,
        url,
        method: method.toLowerCase(),
      },
    };

    if (response.status >= 400) {
      throw createNativeError(
        response.data?.detail ||
          response.data?.message ||
          `Request failed with status ${response.status}`,
        response,
        response.status === 408 ? "ECONNABORTED" : "ERR_BAD_RESPONSE"
      );
    }

    return response;
  } catch (error) {
    if (error?.response) {
      throw error;
    }

    throw createNativeError(
      error?.message || "Native request failed.",
      null,
      error?.message?.toLowerCase?.().includes("timeout") ? "ECONNABORTED" : "ERR_NETWORK"
    );
  }
}

function browserRequest(method, url, data, config = {}) {
  return browserApi.request({
    ...config,
    url,
    method,
    data,
  });
}

async function request(method, url, dataOrConfig, maybeConfig) {
  const hasBody = ["post", "put", "patch"].includes(method);
  const data = hasBody ? dataOrConfig : undefined;
  const config = hasBody ? maybeConfig || {} : dataOrConfig || {};

  // CapacitorHttp on Android/iOS does not reliably serialize browser File/FormData.
  // Multipart uploads must use the WebView's XMLHttpRequest/fetch path instead.
  if (isNativeRuntime() && !isFormDataPayload(data)) {
    return nativeRequest(method, url, data, config);
  }

  return browserRequest(method, url, data, config);
}

export const api = {
  defaults: {
    baseURL: API_BASE_URL,
    timeout: 30000,
  },
  request(config) {
    const method = (config?.method || "get").toLowerCase();
    return request(method, config?.url || "", config?.data, config);
  },
  get(url, config) {
    return request("get", url, config);
  },
  delete(url, config) {
    return request("delete", url, config);
  },
  post(url, data, config) {
    return request("post", url, data, config);
  },
  put(url, data, config) {
    return request("put", url, data, config);
  },
  patch(url, data, config) {
    return request("patch", url, data, config);
  },
};

export function isAuthSessionError(error) {
  const status = Number(error?.response?.status);
  if (status !== 401) {
    return false;
  }

  const detail = error?.response?.data?.detail;
  const normalizedDetail =
    typeof detail === "string"
      ? detail.trim()
      : typeof detail?.message === "string"
        ? detail.message.trim()
        : "";

  return [
    "Authentication required.",
    "Invalid or expired token.",
    "User not found.",
    "Account no longer available.",
  ].includes(normalizedDetail);
}

export function getApiErrorMessage(error, fallbackMessage = "Unexpected error.") {
  const detail = error?.response?.data?.detail;
  const uiLanguage = getUiLanguage();

  if (detail === "Price submissions by users are disabled in this app.") {
    const disabledMessages = {
      "pt-BR": "O envio de precos por usuarios foi desativado neste app.",
      en: "Price submissions by users are disabled in this app.",
      es: "El envio de precios por usuarios fue desactivado en esta app.",
      it: "L'invio di prezzi da parte degli utenti e disattivato in questa app.",
      fr: "L'envoi de prix par les utilisateurs est desactive dans cette app.",
      de: "Preismeldungen durch Nutzer sind in dieser App deaktiviert.",
      "pt-PT": "O envio de precos por utilizadores foi desativado nesta app.",
    };
    return disabledMessages[uiLanguage] || disabledMessages.en;
  }

  if (detail?.code === "subscription_required") {
    const subscriptionMessages = {
      "pt-BR": "Seu periodo gratis terminou. Ative a assinatura para continuar usando o PennyPantry.",
      en: "Your free period ended. Activate the subscription to keep using PennyPantry.",
      es: "Tu periodo gratis termino. Activa la suscripcion para seguir usando PennyPantry.",
      it: "Il periodo gratuito e terminato. Attiva l'abbonamento per continuare a usare PennyPantry.",
      fr: "Votre periode gratuite est terminee. Activez l'abonnement pour continuer a utiliser PennyPantry.",
      de: "Dein Gratiszeitraum ist abgelaufen. Aktiviere das Abo, um PennyPantry weiter zu nutzen.",
      "pt-PT": "O periodo gratuito terminou. Ative a subscricao para continuar a usar o PennyPantry.",
    };
    return subscriptionMessages[uiLanguage] || subscriptionMessages.en;
  }

  if (Array.isArray(detail)) {
    return detail
      .map((item) => item?.msg || item?.message || JSON.stringify(item))
      .filter(Boolean)
      .join(" | ");
  }

  if (detail && typeof detail === "object") {
    return detail.msg || detail.message || JSON.stringify(detail);
  }

  if (typeof detail === "string" && detail.trim()) {
    return detail;
  }

  if (error?.code === "ECONNABORTED") {
    const timeoutMessages = {
      "pt-BR": "A requisicao demorou mais do que o esperado. Tente novamente.",
      en: "The request took too long. Please try again.",
      es: "La solicitud tardo demasiado. Intentalo de nuevo.",
      it: "La richiesta ha impiegato troppo tempo. Riprova.",
      fr: "La requete a pris trop de temps. Reessayez.",
      de: "Die Anfrage hat zu lange gedauert. Bitte versuche es erneut.",
      "pt-PT": "O pedido demorou mais do que o esperado. Tente novamente.",
    };
    return timeoutMessages[uiLanguage] || timeoutMessages.en;
  }

  if (!error?.response) {
    const connectionMessages = {
      "pt-BR": `Nao foi possivel conectar ao servidor do PennyPantry. Confirme se a API esta acessivel em ${API_BASE_URL}.`,
      en: `Could not connect to the PennyPantry server. Confirm the API is reachable at ${API_BASE_URL}.`,
      es: `No se pudo conectar al servidor de PennyPantry. Confirma que la API sea accesible en ${API_BASE_URL}.`,
      it: `Impossibile connettersi al server PennyPantry. Verifica che l'API sia raggiungibile su ${API_BASE_URL}.`,
      fr: `Impossible de se connecter au serveur PennyPantry. Verifiez que l'API est joignable a ${API_BASE_URL}.`,
      de: `Keine Verbindung zum PennyPantry-Server. Prufe, ob die API unter ${API_BASE_URL} erreichbar ist.`,
      "pt-PT": `Nao foi possivel ligar ao servidor PennyPantry. Confirme se a API esta acessivel em ${API_BASE_URL}.`,
    };
    return connectionMessages[uiLanguage] || connectionMessages.en;
  }

  return fallbackMessage;
}
